import type { VideoListItem, VideoDetail, VideoSettings, TuningData, TranslationJob, ResolvedCaption, VideoTagState, SuggestionDecisionResponse } from '../types/videos';
import type { CostsMonthlyResponse } from './dashboard-costs';
import type { PayableInvoice, PaymentAttempt, AttemptResolution, PaymentAttemptStatus, PropertyCardInfo, SetPropertyCardInput, PayablePropertiesResponse } from '../types/payments';
import type { InvoiceListItem } from '../types/invoices';

const BASE = '/api';

async function request(path: string, options: RequestInit = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    let retryAfterSeconds: number | undefined;
    let bodyError: string | undefined;
    try {
      const body = await res.json();
      if (body && typeof body === 'object' && typeof body.retryAfterSeconds === 'number') {
        retryAfterSeconds = body.retryAfterSeconds;
      }
      // The proxied services answer errors as { error: '<message>' } — keep it
      // on the thrown error so callers can surface the service's own wording
      // verbatim (e.g. a PUT /api/payments/cards 400 validation refusal).
      if (body && typeof body === 'object' && typeof body.error === 'string') {
        bodyError = body.error;
      }
    } catch {
      // Non-JSON error body — the status alone is all we have.
    }
    const err = new Error(`API error: ${res.status}`) as Error & { status?: number; retryAfterSeconds?: number; bodyError?: string };
    err.status = res.status;
    err.retryAfterSeconds = retryAfterSeconds;
    err.bodyError = bodyError;
    throw err;
  }
  return res.json();
}

function errorStatus(err: unknown): number | undefined {
  return typeof err === 'object' && err !== null && typeof (err as { status?: unknown }).status === 'number'
    ? (err as { status: number }).status
    : undefined;
}

/**
 * True when the API rejected the request as an authentication problem:
 * missing/expired credentials (401) or the shared sign-in lockout (429).
 * Both are auth-class — neither may ever be rendered as "service
 * unreachable" (CHO-759 review).
 */
export function isAuthError(err: unknown): boolean {
  const status = errorStatus(err);
  return status === 401 || status === 429;
}

function formatLockout(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  if (minutes >= 60) return `${Math.ceil(minutes / 60)} h`;
  return `${minutes} min`;
}

/** Human wording for an auth-class failure, or null for anything else. */
export function authErrorMessage(err: unknown): string | null {
  const status = errorStatus(err);
  if (status === 401) {
    return 'Authentication required — your session has expired. Reload the page and sign in again.';
  }
  if (status === 429) {
    const retry = (err as { retryAfterSeconds?: unknown })?.retryAfterSeconds;
    const wait = typeof retry === 'number' && retry > 0 ? ` for ${formatLockout(retry)}` : '';
    return `Too many failed sign-in attempts — locked out${wait}. Reload the page and sign in again once the lockout lifts.`;
  }
  return null;
}

/**
 * The upstream service's own `error` message when the API relayed one (the
 * proxy and the local routes both answer failures as `{ error }`) — e.g. the
 * card API's per-field 400 refusals, which the UI shows verbatim. Null when
 * the failure carried no message.
 */
export function upstreamErrorMessage(err: unknown): string | null {
  const message = (err as { bodyError?: unknown })?.bodyError;
  return typeof message === 'string' && message.length > 0 ? message : null;
}

function buildQuery(params: Record<string, string>) {
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : '';
}

export const api = {
  getConversations: (params: Record<string, string>, options: RequestInit = {}) => {
    return request(`/conversations${buildQuery(params)}`, options);
  },
  createConversation: (data: any) => request('/conversations', { method: 'POST', body: JSON.stringify(data) }),
  getMessages: (id: number | string) => request(`/conversations/${id}/messages`),
  getDashboard: () => request('/conversations/dashboard'),
  updateRating: (id: number | string, guest_rating: string) => request(`/conversations/${id}/rating`, { method: 'PATCH', body: JSON.stringify({ guest_rating }) }),
  updateColumn: (id: number | string, manual_column: string | null) => request(`/conversations/${id}/column`, { method: 'PATCH', body: JSON.stringify({ manual_column }) }),

  getRules: () => request('/rules'),
  createRule: (data: any) => request('/rules', { method: 'POST', body: JSON.stringify(data) }),
  updateRule: (id: number | string, data: any) => request(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getClaims: () => request('/claims'),
  createClaim: (data: any) => request('/claims', { method: 'POST', body: JSON.stringify(data) }),
  updateClaim: (id: number | string, data: any) => request(`/claims/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getReviews: () => request('/reviews'),
  createReview: (data: any) => request('/reviews', { method: 'POST', body: JSON.stringify(data) }),
  updateReview: (id: number | string, data: any) => request(`/reviews/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  getActivity: () => request('/activity'),

  getProperties: () => request('/properties'),
  getBookings: (params: Record<string, string>) => {
    return request(`/bookings${buildQuery(params)}`);
  },
  getBookingsSummary: () => request('/bookings/summary'),
  getCostsMonthly: (params: { from?: string; to?: string } = {}) => {
    return request(`/costs/monthly${buildQuery(params as Record<string, string>)}`) as Promise<CostsMonthlyResponse>;
  },
  importBookings: () => request('/bookings/import', { method: 'POST' }),

  getPastGuests: (params: { property_id?: string | number } = {}) => {
    return request(`/guests/past${buildQuery(params as Record<string, string>)}`);
  },

  getAgents: () => request('/agents'),

  // Video Library
  getVideos: () => request('/videos') as Promise<{ videos: VideoListItem[] }>,
  getVideo: (stem: string, lang?: string | null) =>
    request(`/videos/${encodeURIComponent(stem)}${lang ? buildQuery({ lang }) : ''}`) as Promise<VideoDetail>,
  getVideoJobs: () => request('/videos/jobs') as Promise<{ jobs: any[]; translation_jobs: TranslationJob[] }>,
  getVideoSettings: () => request('/videos/settings') as Promise<VideoSettings>,
  updateVideoSettings: (data: Partial<VideoSettings>) => request('/videos/settings', { method: 'PUT', body: JSON.stringify(data) }) as Promise<VideoSettings>,
  getVideoTuning: () => request('/videos/tuning') as Promise<TuningData>,
  getVideoTags: () => request('/videos/tags') as Promise<{ tags: string[] }>,
  transcribeVideo: (stem: string, force = false) => request(`/videos/${encodeURIComponent(stem)}/transcribe`, { method: 'POST', body: JSON.stringify({ force }) }),
  transcribeBackfill: () => request('/videos/transcribe-backfill', { method: 'POST' }) as Promise<{ enqueued: string[] }>,
  translateVideo: (stem: string, opts: { target_lang?: string; force?: boolean } = {}) =>
    request(`/videos/${encodeURIComponent(stem)}/translate`, { method: 'POST', body: JSON.stringify(opts) }) as Promise<{ job: TranslationJob }>,
  translateBackfill: () =>
    request('/videos/translate-backfill', { method: 'POST' }) as Promise<{ enqueued: string[]; skipped: { stem: string; reason: string }[] }>,
  updateVideoNotes: (stem: string, body: string) => request(`/videos/${encodeURIComponent(stem)}/notes`, { method: 'PUT', body: JSON.stringify({ body }) }),
  updateVideoTags: (stem: string, tags: string[]) => request(`/videos/${encodeURIComponent(stem)}/tags`, { method: 'PUT', body: JSON.stringify({ tags }) }) as Promise<{ tags: string[] }>,
  getVideoTagState: (stem: string) =>
    request(`/videos/${encodeURIComponent(stem)}/tags`) as Promise<VideoTagState>,
  decideSuggestion: (stem: string, id: number, decision: 'accept' | 'reject') =>
    request(`/videos/${encodeURIComponent(stem)}/suggestions/${id}/${decision}`, { method: 'POST' }) as Promise<SuggestionDecisionResponse>,
  updateVideoCaption: (stem: string, body: string) =>
    request(`/videos/${encodeURIComponent(stem)}/caption`, { method: 'PUT', body: JSON.stringify({ body }) }) as Promise<ResolvedCaption>,
  getCaptionCoverage: () =>
    request('/videos/caption-coverage') as Promise<{ total: number; with_caption: number; missing: { stem: string; title: string }[] }>,
  updateVideoTitle: (stem: string, title: string) =>
    request(`/videos/${encodeURIComponent(stem)}/title`, { method: 'PUT', body: JSON.stringify({ title }) }) as Promise<{ title: string; title_source: VideoDetail['title_source'] }>,

  // Invoices tab (CHO-761) — the bill inbox behind /invoices. Served by this
  // app's own GET /api/invoices (server/routes/invoice-list.ts, CHO-760),
  // gated by paymentsReadAuth like the payment reads. Fetch all rows; the
  // unit filter is multi-select, so filtering stays client-side.
  getInvoices: () => request('/invoices') as Promise<{ invoices: InvoiceListItem[] }>,

  // Invoice payments (CHO-754) — proxied to the invoice-payments service
  // (CHO-753). Card references are last four digits only; there is no card
  // input anywhere in this client.
  //
  // Contract (verified against invoice-payments@main):
  //   POST /api/pay/:invoiceId      → 202 { attemptId, status } — NOT an attempt row
  //   resolve body                  → { outcome: 'succeeded'|'failed', note } (note required)
  getPaymentQueue: () => request('/payments/queue') as Promise<{ invoices: PayableInvoice[] }>,
  // CHO-799 — which properties the payment service can pay (confirmed
  // invoice_account_map rows bound to an allowlisted biller domain). Served by
  // this app's own router under the /api/payments prefix, in front of the
  // proxy; the card-slot set on the payments page is driven by this list.
  getPayableProperties: () => request('/payments/payable-properties') as Promise<PayablePropertiesResponse>,
  initiatePayment: (invoiceId: number | string) =>
    request(`/payments/pay/${encodeURIComponent(String(invoiceId))}`, { method: 'POST', body: JSON.stringify({}) }) as Promise<{ attemptId: string; status: PaymentAttemptStatus }>,
  getPaymentAttempt: (id: string) =>
    request(`/payments/attempt/${encodeURIComponent(id)}`) as Promise<PaymentAttempt>,
  getPaymentAttempts: () => request('/payments/attempts') as Promise<{ attempts: PaymentAttempt[] }>,
  resolvePaymentAttempt: (id: string, outcome: AttemptResolution, note: string) =>
    request(`/payments/attempt/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ outcome, note }),
    }) as Promise<PaymentAttempt>,

  // Property cards (CHO-771) — write-only card management proxied to the
  // invoice-payments card API (CHO-763). The bearer token is attached
  // server-side; it never exists in this bundle. Responses are last4/source/
  // configuredAt only — no endpoint ever returns a PAN, expiry or CVV.
  //
  // Contract (verified against invoice-payments cho-763, PR #3):
  //   GET    /api/cards/:propertyId  → PropertyCardInfo
  //   PUT    /api/cards/:propertyId  body {pan, expiry, cvv} → PropertyCardInfo (400 {error} passthrough)
  //   DELETE /api/cards/:propertyId  → PropertyCardInfo (idempotent, always 200)
  getPropertyCard: (propertyId: number) =>
    request(`/payments/cards/${encodeURIComponent(String(propertyId))}`) as Promise<PropertyCardInfo>,
  setPropertyCard: (propertyId: number, fields: SetPropertyCardInput) =>
    request(`/payments/cards/${encodeURIComponent(String(propertyId))}`, {
      method: 'PUT',
      body: JSON.stringify(fields),
    }) as Promise<PropertyCardInfo>,
  deletePropertyCard: (propertyId: number) =>
    request(`/payments/cards/${encodeURIComponent(String(propertyId))}`, { method: 'DELETE' }) as Promise<PropertyCardInfo>,
};
